import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";

const CATEGORIES = ["Corporate Law", "Civil Litigation", "Family Law", "Real Estate", "Criminal Defense", "Estate Planning", "Legal News"];
const inputCls = "bg-white border border-border focus:border-gold px-3 py-2.5 w-full outline-none transition text-sm rounded-none";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

export default function BlogEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", slug: "", excerpt: "", content: "", cover_image: "",
    category: "Legal News", tags: "", seo_title: "", seo_description: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isNew) {
      api.get(`/blogs/${id}`).then(({ data }) => {
        setForm({
          title: data.title, slug: data.slug, excerpt: data.excerpt, content: data.content,
          cover_image: data.cover_image, category: data.category,
          tags: (data.tags || []).join(", "), seo_title: data.seo_title, seo_description: data.seo_description,
        });
        setLoading(false);
      }).catch(() => {
        toast.error("Post not found");
        navigate("/dashboard/blog");
      });
    }
  }, [id, isNew, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (publish) => {
    const errs = {};
    if (!form.title.trim()) errs.title = "Title is required";
    if (!form.content || form.content === "<p><br></p>") errs.content = "Content is required";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: publish ? "published" : "draft",
    };
    try {
      if (isNew) await api.post("/blogs", payload);
      else await api.put(`/blogs/${id}`, payload);
      toast.success(publish ? "Post published" : "Draft saved");
      navigate("/dashboard/blog");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading label="Loading post" />;

  return (
    <div data-testid="blog-editor-page">
      <button onClick={() => navigate("/dashboard/blog")} data-testid="back-to-blog"
        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-navy transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> All Posts
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-border p-6 sm:p-8">
            <Field label="Post Title *" error={errors.title} htmlFor="post-title">
              <input id="post-title" className={`${inputCls} font-serif text-xl`} value={form.title} onChange={set("title")}
                data-testid="post-title-input" placeholder="An opinionated, useful headline…" />
            </Field>
            <div className="mt-6">
              <Field label="Excerpt" htmlFor="post-excerpt">
                <textarea id="post-excerpt" rows={2} className={`${inputCls} resize-none`} value={form.excerpt} onChange={set("excerpt")}
                  data-testid="post-excerpt-input" placeholder="One or two sentences shown on cards and search results…" />
              </Field>
            </div>
            <div className="mt-6">
              <label className="text-xs font-semibold uppercase tracking-widest text-navy-700 block mb-1.5">Content *</label>
              {errors.content && <p className="text-xs text-red-600 mb-1.5" data-testid="post-content-error">{errors.content}</p>}
              <div data-testid="post-content-editor" className="bg-white border border-border">
                <ReactQuill theme="snow" value={form.content} onChange={(v) => setForm({ ...form, content: v })}
                  modules={QUILL_MODULES} placeholder="Write the article…" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-border p-6 sm:p-8">
            <h3 className="font-serif text-xl font-semibold text-navy mb-5">SEO</h3>
            <div className="space-y-5">
              <Field label="Meta Title" htmlFor="post-seo-title">
                <input id="post-seo-title" className={inputCls} value={form.seo_title} onChange={set("seo_title")}
                  data-testid="post-seo-title-input" placeholder={form.title || "Defaults to post title"} />
              </Field>
              <Field label="Meta Description" htmlFor="post-seo-description">
                <textarea id="post-seo-description" rows={2} className={`${inputCls} resize-none`} value={form.seo_description} onChange={set("seo_description")}
                  data-testid="post-seo-description-input" placeholder="155 characters that earn the click…" />
              </Field>
              <div className="bg-muted/60 p-4 border border-border">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Search preview</p>
                <p className="text-blue-800 text-sm font-medium">{form.seo_title || form.title || "Post title"}</p>
                <p className="text-emerald-800 text-xs">wanarlaw.com/blog/{form.slug || "post-slug"}</p>
                <p className="text-xs text-muted-foreground mt-1">{form.seo_description || form.excerpt || "Meta description appears here…"}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-border p-6 space-y-5">
            <Field label="URL Slug" htmlFor="post-slug">
              <input id="post-slug" className={inputCls} value={form.slug} onChange={set("slug")}
                data-testid="post-slug-input" placeholder="auto-generated-from-title" />
            </Field>
            <Field label="Category" htmlFor="post-category">
              <select id="post-category" className={inputCls} value={form.category} onChange={set("category")} data-testid="post-category-select">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Tags (comma separated)" htmlFor="post-tags">
              <input id="post-tags" className={inputCls} value={form.tags} onChange={set("tags")}
                data-testid="post-tags-input" placeholder="litigation, illinois, tips" />
            </Field>
          </div>

          <div className="bg-white border border-border p-6">
            <Field label="Cover Image URL" htmlFor="post-cover">
              <input id="post-cover" className={inputCls} value={form.cover_image} onChange={set("cover_image")}
                data-testid="post-cover-input" placeholder="https://…" />
            </Field>
            {form.cover_image ? (
              <img src={form.cover_image} alt="Cover preview" data-testid="post-cover-preview"
                className="w-full h-40 object-cover mt-4 border border-border" />
            ) : (
              <div className="w-full h-40 mt-4 border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">
                Cover preview
              </div>
            )}
          </div>

          <div className="bg-navy text-ivory p-6 space-y-3">
            <Button onClick={() => save(true)} disabled={saving} data-testid="post-publish-btn"
              className="w-full bg-gold text-navy hover:bg-gold-600 rounded-none font-semibold uppercase tracking-wider text-xs">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish Post"}
            </Button>
            <Button onClick={() => save(false)} disabled={saving} variant="outline" data-testid="post-save-draft-btn"
              className="w-full border-ivory/40 text-ivory hover:bg-ivory/10 hover:text-ivory rounded-none bg-transparent font-semibold uppercase tracking-wider text-xs">
              Save as Draft
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
